/**
 * AbortableAsync class
 * 
 * Wrap Promise to run async task that can be aborted and just forget about it.
 */
export default class AbortableAsync  {
  constructor() {
    this._done = false;
    this.__resolve = null;
    this.__reject = null;
    this.promise = new Promise((resolve, reject) => {
      this.__resolve = resolve;
      this.__reject = reject;
    });
    this.promise.catch((...args) => {
      // eslint-disable-next-line no-console
      console.log('AbortableSync error', args);
    });
  }

  /**
   * Promise.prototype.then wrapper.
   * 
   * @param  {...any} args 
   * @returns Promise
   */
  then(...args) {
    return Promise.prototype.then.apply(this.promise, args);
  }

  /**
   * Promise.prototype.catch wrapper.
   * 
   * @param  {...any} args 
   * @returns Promise
   */
  catch(...args) {
    return Promise.prototype.catch.apply(this.promise, args);
  }

  /**
   * Promise.prototype.finally wrapper.
   * 
   * @param  {...any} args 
   * @returns Promise
   */
  finally(...args) {
    return Promise.prototype.finally.apply(this.promise, args);
  }

  /**
   * Abort this task.
   * 
   * @returns AbortableAsync
   */
  abort() {
    if (!this._done) {
      this.reject('aborted');
    }

    return this;
  }

  /**
   * Resolve this task.
   * 
   * @returns AbortableAsync
   */
  resolve(...args) {
    if (!this._done) {
      this.__resolve.apply(null, args);
    }
    this._done = true;

    return this;
  }

  /**
   * Reject this task.
   * 
   * @returns AbortableAsync
   */
  reject(...args) {
    if (!this._done) {
      this.__reject.apply(null, args);
    }
    this._done = true;

    return this;
  }

  /**
   * Start task.
   * 
   * *Note that caller must resolve/reject the task manually.*
   * 
   * @param {Function} cb 
   * @param {Int} [timeout]
   * @returns AbortableAsync
   */
  run(cb, timeout) {
    const asyncCallback = async () => {
      cb();
    };

    if (timeout && timeout > 0) {
      this._timeout = window.setTimeout(() => {
        this.reject('timeout');
      });
    }

    try {
      asyncCallback();
    } catch (err) {
      this.reject(err);
    }

    return this;
  }
}